import styled, { css } from "styled-components";

import { bettoSelectedSurface } from "../bettoSelectedSurface";

import chevronRightIcon from "@layouts/betto/assets/chevronRight.svg";
import { CardHoverOverlay, CardPlayIcon } from "@pages/home/betto/casinoSlider/elements";

const glassCardBorder = css`
	isolation: isolate;

	&::after {
		content: "";
		position: absolute;
		inset: 0;
		border-radius: inherit;
		border: 1px solid #ffffff;
		mix-blend-mode: overlay;
		pointer-events: none;
		z-index: 2;
	}
`;

export const BettoExploreGameCardWrap = styled.div`
	position: relative;
	aspect-ratio: ${({ $aspectRatio = "1 / 1" }) => $aspectRatio};
	border-radius: 12px;
	box-sizing: border-box;
	overflow: hidden;
	cursor: pointer;
	z-index: 0;
	background: #171c2a;
	transition: transform 0.2s ease-in-out;
	${glassCardBorder}

	&:hover {
		transform: translateY(-12px);
		z-index: 2;
	}

	&:hover ${CardHoverOverlay} {
		opacity: 1;
		pointer-events: auto;
	}

	&:hover ${CardPlayIcon} {
		transform: translate(-50%, -50%);
	}
`;

export const BettoExploreRoot = styled.div`
	position: fixed;
	inset: 0;
	z-index: 10000;
	display: ${({ $open }) => ($open ? "flex" : "none")};
	flex-direction: column;
	align-items: center;
`;

export const BettoExploreOverlay = styled.button`
	position: absolute;
	inset: 0;
	border: none;
	padding: 0;
	background: rgba(5, 8, 16, 0.97);
	backdrop-filter: blur(8px);
	cursor: pointer;
`;

export const BettoExplorePanel = styled.div`
	position: relative;
	z-index: 1;
	display: flex;
	flex-direction: column;
	width: 100%;
	max-width: 1104px;
	height: 100%;
	padding: 24px 16px 32px;
	overflow-y: auto;
	box-sizing: border-box;

	@media (min-width: 768px) {
		padding: 32px 24px 40px;
	}
`;

export const BettoExploreHeader = styled.div`
	display: flex;
	align-items: center;
	justify-content: space-between;
	width: 100%;
	margin-bottom: 24px;
`;

export const BettoExploreTitle = styled.h2`
	margin: 0;
	color: #e5f0fe;
	font-family: var(--bt-font-family);
	font-size: 22px;
	font-weight: 600;
	line-height: 1.1;
	letter-spacing: -1.32px;
	text-transform: capitalize;
`;

export const BettoExploreCloseButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 34px;
	height: 34px;
	padding: 8px;
	border: none;
	border-radius: 10px;
	background: #2b3245;
	color: #e5f0fe;
	cursor: pointer;

	svg {
		display: block;
		width: 16px;
		height: 16px;
	}
`;

export const BettoExploreSearchSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 24px;
	width: 100%;
	margin-bottom: 32px;
	overflow: visible;
`;

export const BettoExploreSearchBar = styled.div`
	display: flex;
	align-items: stretch;
	gap: 10px;
	width: 100%;
	min-height: 52px;
	padding: 0 14px;
	box-sizing: border-box;
	border-radius: 10px;
	border: 1px solid ${({ $focused }) => ($focused ? "#0362d4" : "#343946")};
	background: #1f2536;
`;

export const BettoExploreTypeSelector = styled.div`
	position: relative;
	display: flex;
	align-items: center;
	flex-shrink: 0;
	align-self: stretch;
`;

export const BettoExploreTypeButton = styled.button`
	display: inline-flex;
	align-items: center;
	gap: 12px;
	height: 100%;
	padding: 0 20px 0 0;
	border: none;
	border-right: 1px solid #343946;
	background: transparent;
	color: #e5f0fe;
	font-family: var(--bt-font-family);
	font-size: 16px;
	font-weight: 600;
	line-height: 1.4;
	letter-spacing: -0.48px;
	cursor: pointer;
	white-space: nowrap;
	outline: none;

	&:focus,
	&:focus-visible {
		outline: none;
		box-shadow: none;
	}
`;

export const BettoExploreTypeChevron = styled.img`
	display: block;
	width: 24px;
	height: 24px;
	flex-shrink: 0;
	transition: transform 0.2s ease;
	transform: ${({ $open }) => ($open ? "rotate(180deg)" : "none")};
`;

export const BettoExploreTypeMenu = styled.div`
	position: absolute;
	top: calc(100% + 8px);
	left: 0;
	z-index: 5;
	min-width: 100%;
	padding: 4px;
	border-radius: 10px;
	background: #171c2a;
	border: 1px solid #343946;
	box-shadow: 0 8px 24px rgba(0, 0, 0, 0.35);
`;

export const BettoExploreTypeOption = styled.button`
	display: block;
	width: 100%;
	padding: 10px 12px;
	border: none;
	border-radius: 8px;
	background: transparent;
	color: ${({ $active }) => ($active ? "#e5f0fe" : "#abb4cf")};
	font-family: var(--bt-font-family);
	font-size: 14px;
	font-weight: 400;
	line-height: 1.4;
	text-align: left;
	cursor: pointer;
	position: relative;
	isolation: isolate;
	overflow: hidden;

	${({ $active }) =>
		$active
			? css`
					${bettoSelectedSurface}
				`
			: css`
					&:hover:not(:disabled) {
						background: #202534;
						color: #e5f0fe;
					}
				`}
`;

export const BettoExploreSearchField = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	flex: 1;
	min-width: 0;
	align-self: center;
`;

export const BettoExploreSearchIcon = styled.img`
	flex-shrink: 0;
	display: block;
	width: 24px;
	height: 24px;
`;

export const BettoExploreSearchInput = styled.input`
	flex: 1;
	min-width: 0;
	border: none;
	outline: none;
	background: transparent;
	color: #e5f0fe;
	font-family: var(--bt-font-family);
	font-size: 14px;
	font-weight: 400;
	line-height: 1.4;
	letter-spacing: -0.42px;

	&::placeholder {
		color: #858faf;
	}
`;

export const BettoExploreClearButton = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	width: 24px;
	height: 24px;
	padding: 0;
	border: none;
	background: transparent;
	color: #858faf;
	cursor: pointer;
	font-size: 18px;
	line-height: 1;
`;

export const BettoExploreFilterTrack = styled.div`
	position: relative;
	width: 100%;
	min-height: 54px;
	padding: 8px 4px;
	box-sizing: border-box;
	border-radius: 14px;
	background: #171c2a;
	overflow: hidden;
`;

export const BettoExploreFilterScroll = styled.div`
	display: flex;
	align-items: center;
	gap: 8px;
	overflow-x: auto;
	overflow-y: hidden;
	scroll-behavior: smooth;
	padding-right: ${({ $hasScrollPadding }) => ($hasScrollPadding ? "48px" : "0")};
	-ms-overflow-style: none;
	scrollbar-width: none;

	& > * {
		flex: 0 0 auto;
		width: max-content;
		max-width: max-content;
		min-width: 0;
	}

	&::-webkit-scrollbar {
		display: none;
	}
`;

export const BettoExploreFilterPill = styled.button`
	display: inline-flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	flex: 0 0 auto;
	flex-shrink: 0;
	box-sizing: border-box;
	width: max-content;
	max-width: max-content;
	height: 38px;
	padding: 0 12px;
	border: none;
	border-radius: 10px;
	background: transparent;
	color: ${({ $active }) => ($active ? "#e5f0fe" : "#abb4cf")};
	font-family: var(--bt-font-family);
	font-size: 14px;
	font-weight: 400;
	line-height: 1;
	cursor: pointer;
	white-space: nowrap;
	position: relative;
	isolation: isolate;
	overflow: hidden;
	transition:
		background 0.2s ease,
		color 0.2s ease;

	${({ $active }) =>
		$active &&
		css`
			${bettoSelectedSurface}
		`}

	&:hover:not(:disabled) {
		background: #202534;
		color: #e5f0fe;
	}

	span {
		display: inline-flex;
		align-items: center;
		line-height: 1;
	}
`;

export const BettoExploreFilterIcon = styled.img`
	flex-shrink: 0;
	display: block;
	width: 14px;
	height: 14px;
	object-fit: contain;
	transition: filter 0.2s ease;
`;

export const BettoExploreScrollFade = styled.div`
	position: absolute;
	top: 50%;
	right: 0;
	z-index: 1;
	width: 60px;
	height: 46px;
	background: #171c2a;
	pointer-events: none;
	transform: translateY(-50%);
`;

export const BettoExploreScrollButton = styled.button`
	position: absolute;
	top: 50%;
	right: 6px;
	z-index: 2;
	width: 34px;
	height: 34px;
	padding: 0;
	border: none;
	border-radius: 10px;
	background: transparent;
	background-image: url(${chevronRightIcon});
	background-size: contain;
	background-position: center;
	background-repeat: no-repeat;
	cursor: pointer;
	transform: translateY(-50%);
	transition: opacity 0.2s ease;

	&:disabled {
		opacity: 0.35;
		cursor: default;
	}
`;

export const BettoExploreGridSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 12px;
	width: 100%;
	padding: 12px 0 18px;
`;

export const BettoExploreGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
	width: 100%;

	@media (min-width: 640px) {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}

	@media (min-width: 900px) {
		grid-template-columns: repeat(5, minmax(0, 1fr));
	}

	@media (min-width: 1100px) {
		grid-template-columns: repeat(7, minmax(0, 1fr));
	}
`;

export const BettoExploreEmpty = styled.p`
	margin: 24px 0 0;
	color: #858faf;
	font-family: var(--bt-font-family);
	font-size: 14px;
	line-height: 1.4;
	text-align: center;
`;

export const BettoExplorePlaceholderGrid = styled.div`
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 12px;
	width: 100%;
	pointer-events: none;

	@media (min-width: 640px) {
		grid-template-columns: repeat(4, minmax(0, 1fr));
	}

	@media (min-width: 900px) {
		grid-template-columns: repeat(5, minmax(0, 1fr));
	}

	@media (min-width: 1100px) {
		grid-template-columns: repeat(7, minmax(0, 1fr));
	}
`;

export const BettoExplorePlaceholderCard = styled.div`
	aspect-ratio: 1 / 1;
	border-radius: 12px;
	background: #171c2a;
	opacity: ${({ $dimmed }) => ($dimmed ? 0.28 : 0.45)};
`;

export const BettoExploreLoaderWrap = styled.div`
	display: flex;
	justify-content: center;
	padding: 48px 0;
`;

export const BettoExplorePagination = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	width: 100%;
	margin-top: 24px;
`;

export const BettoExplorePageNavIcon = styled.img`
	display: block;
	width: 30px;
	height: 30px;
	transition: filter 0.2s ease;
	transform: ${({ $mirrored }) => ($mirrored ? "rotate(180deg)" : "none")};
`;

export const BettoExplorePageNav = styled.button`
	display: flex;
	align-items: center;
	justify-content: center;
	width: 30px;
	height: 30px;
	padding: 0;
	border: none;
	background: transparent;
	cursor: pointer;
	flex-shrink: 0;
	transition: opacity 0.2s ease;

	&:hover:not(:disabled) ${BettoExplorePageNavIcon} {
		filter: brightness(1.25) drop-shadow(0 0 3px rgba(229, 240, 254, 0.65));
	}

	&:disabled {
		opacity: 0.4;
		cursor: default;
	}
`;

export const BettoExplorePageBox = styled.div`
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 4px;
	border-radius: 10px;
	background: #171c2a;
`;

export const BettoExplorePageCurrent = styled.div`
	display: flex;
	align-items: center;
	justify-content: center;
	min-width: 28px;
	padding: 6px 9px 3px;
	border-radius: 6px;
	background: #303543;
	color: #e5f0fe;
	font-family: var(--bt-font-family);
	font-size: 14px;
	font-weight: 600;
	line-height: 1;
`;

export const BettoExplorePageTotal = styled.span`
	padding: 5px 6px 3px;
	color: #858faf;
	font-family: var(--bt-font-family);
	font-size: 14px;
	font-weight: 400;
	line-height: 1;
	white-space: nowrap;
`;
